// src/components/LoginModal.js
// FIXED: password show/hide toggle (persistent change — do not remove)
// Architecture: no props, opens on 'open-login' event, uses AuthContext
// ADDED: fires 'open-role-picker' event after successful login/signup so RolePickerModal can show
// ADDED: date_of_birth field — required at signup (Apple/COPPA compliance)
// FIX: getStoredToken now reads localStorage["gt_token"] first. Was sessionStorage || localStorage["token"] — missed gt_token on hard-refresh.
// FIXED: parental-consent age threshold is now country-aware. Frontend fetches
//        /api/age-threshold on mount and uses the returned value. Backend
//        (functions/api/signup.js) is the source of truth; this endpoint
//        mirrors its AGE_THRESHOLDS table (IN=18, DE/NL/AT/HU/HR=16, FR=15,
//        ES/IT/CY/LT/KR=14, default 13). Previously hardcoded to 13.

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { API_BASE } from "../utils/api";
import { Capacitor } from "@capacitor/core";

// Load Apple Sign In only on native — safe require, no crash on web
var SignInWithApple = null;
try {
  if (Capacitor.isNativePlatform()) {
    SignInWithApple = require("@capacitor-community/apple-sign-in").SignInWithApple;
  }
} catch(e) {}

export function getStoredToken() {
  try { return localStorage.getItem("gt_token") || sessionStorage.getItem("gt_token") || localStorage.getItem("token") || null; }
  catch (e) { return null; }
}

export function getStoredUser() {
  try { const raw = sessionStorage.getItem("gt_user"); return raw ? JSON.parse(raw) : null; }
  catch (e) { return null; }
}

export function clearAuth() {
  try {
    sessionStorage.removeItem("gt_token");
    sessionStorage.removeItem("gt_user");
    localStorage.removeItem("token");
  } catch (e) {}
}

async function safeFetch(url, options) {
  const res = await fetch(API_BASE + url, options);
  let data = {};
  try { data = await res.json(); } catch (e) {}
  return { ok: res.ok, status: res.status, data };
}

// Returns age in years from a "YYYY-MM-DD" string, or null if invalid
function calcAge(dob) {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

// Today's date in YYYY-MM-DD for the date input's max attribute
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// Minimum date: no one is over 100 years old
function minDateISO() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 100);
  return d.toISOString().slice(0, 10);
}

// ── Eye icon SVG ──────────────────────────────────────────────────────────────
function EyeIcon({ open }) {
  return open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

export default function LoginModal() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [open, setOpen]           = useState(false);
  const [tab, setTab]             = useState("login");
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [alias, setAlias]         = useState("");
  const [dob, setDob]             = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [showPass, setShowPass]   = useState(false);
  const [showPass2, setShowPass2] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  // After a successful minor signup, show a confirmation instead of logging in
  const [consentPending, setConsentPending] = useState(false);
  // Country-aware age-of-consent threshold. Defaults to 13 (US COPPA) until
  // /api/age-threshold returns the real value for the user's country.
  const [ageThreshold, setAgeThreshold] = useState(13);
  // Set true if the backend rejects signup asking for a parent email. Covers
  // the case where the client threshold was wrong (e.g. /api/age-threshold
  // failed and we fell back to 13) — forces the parent field to appear so the
  // user can supply it instead of being stuck on "parent email required".
  const [forceParentField, setForceParentField] = useState(false);

  const firstInputRef = useRef(null);

  // Derived — recalculated on every render from the dob field
  const age     = calcAge(dob);
  const isMinor = age !== null && age < ageThreshold;
  // Show/require the parent field when the user is a minor by the (country)
  // threshold OR when the backend has told us it's needed.
  const needsParent = isMinor || forceParentField;
  // Hard floor: under-13 cannot self-register on any platform (COPPA hard floor).
  // ageThreshold may be higher (e.g. 18 in India) but 13 is the global minimum.
  const ageOk   = age !== null && age >= 13;

  // Fetch the country-specific age threshold once on mount. If the call
  // fails we stay on the conservative US-COPPA default of 13.
  useEffect(() => {
    let cancelled = false;
    fetch(API_BASE + "/api/age-threshold")
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        if (!cancelled && d && typeof d.threshold === "number") {
          setAgeThreshold(d.threshold);
        }
      })
      .catch(function() {});
    return function() { cancelled = true; };
  }, []);

  useEffect(() => {
    function handleOpen() {
      setTab("login");
      setEmail(""); setPassword(""); setAlias(""); setDob(""); setParentEmail("");
      setError(""); setShowPass(false); setShowPass2(false); setConsentPending(false); setForceParentField(false);
      setOpen(true);
    }
    window.addEventListener("open-login", handleOpen);
    return () => window.removeEventListener("open-login", handleOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  useEffect(() => {
    if (open && firstInputRef.current) {
      setTimeout(() => firstInputRef.current?.focus(), 50);
    }
  }, [open, tab]);

  function handleTabSwitch(newTab) {
    setTab(newTab);
    setEmail(""); setPassword(""); setAlias(""); setDob(""); setParentEmail("");
    setError(""); setShowPass(false); setShowPass2(false); setConsentPending(false); setForceParentField(false);
  }

  

  function storeSession(token, user) {
    login(token, user);
    try {
      sessionStorage.setItem("gt_token", token);
      sessionStorage.setItem("gt_user", JSON.stringify(user));
      localStorage.setItem("gt_token", token);
    } catch (e) {}
    // Tell App.jsx the user is now authenticated, so it can flush any
    // device push token that was registered before login completed.
    try { window.dispatchEvent(new Event("gt-login")); } catch (e) {}
  }

  // ── Login ─────────────────────────────────────────────────────────────────
  async function handleLogin(e) {
    e.preventDefault();
    if (loading) return;
    setError(""); setLoading(true);
    try {
      const { ok, data } = await safeFetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!ok) { setError(data.error || "Invalid email or password"); return; }
      storeSession(data.token, data.user);
      setOpen(false);
      window.dispatchEvent(new CustomEvent("open-role-picker", {
        detail: { user: data.user, isNewSignup: false },
      }));
    } catch (err) {
      setError("Network error — please check your connection");
    } finally {
      setLoading(false);
    }
  }

  // ── Signup ────────────────────────────────────────────────────────────────
  async function handleSignup(e) {
    e.preventDefault();
    if (loading) return;
    setError("");

    // Client-side validation
    if (password.length < 8)   { setError("Password must be at least 8 characters"); return; }
    if (!alias.trim())          { setError("Alias is required"); return; }
    if (!dob)                   { setError("Date of birth is required"); return; }
    if (!ageOk)                 { setError(age !== null && age < 13 ? "You must be at least 13 to create your own Gametrait account. If you're under 13, ask a parent or guardian to add you from their Parent Dashboard." : "Please enter a valid date of birth"); return; }
    if (needsParent && !parentEmail.trim()) {
      setError("A parent or guardian email is required for users under " + ageThreshold);
      return;
    }
    if (needsParent && parentEmail.trim().toLowerCase() === email.trim().toLowerCase()) {
      setError("Parent email must be different from your own email");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email:         email.trim(),
        password,
        alias:         alias.trim(),
        date_of_birth: dob,
      };
      if (needsParent) payload.parent_email = parentEmail.trim().toLowerCase();

      const { ok, status, data } = await safeFetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!ok) {
        // If the backend says a parent email is required but we never showed
        // the field (client threshold was wrong because the age-threshold fetch
        // failed), force the field to appear so the user can supply it and
        // resubmit — rather than being stuck on the error with nowhere to type.
        var errMsg = (data && data.error) || "";
        // Under-13 cannot self-register — show the guidance message, not the code.
        if (data && (data.error === "under_13_block" || data.code === "under_13_block")) {
          setError((data && data.message) || "You must be at least 13 to create your own account. Ask a parent or guardian to add you from their Parent Dashboard.");
          return;
        }
        if (status === 400 && /parent|guardian/i.test(errMsg)) {
          setForceParentField(true);
          setError(errMsg || "A parent or guardian email is required.");
          return;
        }
        setError(
          status === 409
            ? (errMsg || "Email or alias already taken — try logging in")
            : (errMsg || "Signup failed — please try again")
        );
        return;
      }

      // Minor path: backend returns 202 + { status: "consent_pending" }
      // Account is created but unverified until parent approves
      if (data.status === "consent_pending") {
        setConsentPending(true);
        return;
      }

      // Adult / teen-above-threshold path: token + user returned immediately
      if (!data.token || !data.user) {
        setError("Account created — please log in");
        handleTabSwitch("login");
        return;
      }

      storeSession(data.token, data.user);
      sessionStorage.setItem("gt_just_signed_up", "1"); // Home page on first open
      setOpen(false);
      window.dispatchEvent(new CustomEvent("open-role-picker", {
        detail: { user: data.user, isNewSignup: true },
      }));

    } catch (err) {
      setError("Network error — please check your connection");
    } finally {
      setLoading(false);
    }
  }

  async function handleAppleSignIn() {
    if (!SignInWithApple || appleLoading) return;
    setAppleLoading(true);
    setError("");
    try {
      var result = await SignInWithApple.authorize({
        clientId:    "com.gametrait.ios",
        redirectURI: "https://gametrait.com",
        scopes:      "email name",
      });
      var r = result.response || result;
      var { ok, data } = await safeFetch("/api/auth-apple", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identityToken: r.identityToken,
          user:          r.user       || "",
          email:         r.email      || "",
          givenName:     r.givenName  || "",
          familyName:    r.familyName || "",
        }),
      });
      if (!ok) {
        setError(data.error === "no_account"
          ? "No Gametrait account found for this Apple ID. Sign up with email first."
          : data.error || "Apple sign-in failed.");
        return;
      }
      storeSession(data.token, data.user);
      setOpen(false);
      window.dispatchEvent(new Event("open-role-picker"));
    } catch (e) {
      if (e && e.error !== "popup_closed_by_user") {
        setError("Apple sign-in failed. Try again.");
      }
    } finally {
      setAppleLoading(false);
    }
  }

  if (!open) return null;

  const inputCls    = "w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-lime-400 transition-colors";
  const eyeBtnCls   = "absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors";

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={function(e) { if (e.target === e.currentTarget) { setOpen(false); setTab("login"); } }}>
      <div className="absolute inset-0 bg-black/80" onClick={function() { setOpen(false); setTab("login"); }} />
      <div className="relative bg-zinc-900 border border-zinc-800 rounded-t-2xl sm:rounded-2xl w-full max-w-sm mx-0 sm:mx-4 z-10 max-h-[92vh] overflow-y-auto">

        <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(false); }}
          className="absolute top-4 right-4 text-zinc-400 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center"
          aria-label="Close">
          &times;
        </button>

        {/* ── Consent pending confirmation ───────────────────────────────── */}
        {consentPending ? (
          <div className="text-center py-4">
            <div className="text-4xl mb-4">📧</div>
            <p className="text-lime-400 text-xs font-mono uppercase tracking-widest mb-2">Almost there</p>
            <h2 className="text-white text-lg font-semibold mb-3">Check your parent&apos;s inbox</h2>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              We&apos;ve sent a consent email to{" "}
              <span className="text-white font-medium">{parentEmail}</span>.
              Your account will be activated once they approve it.
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full bg-lime-400 hover:bg-lime-300 text-black font-semibold rounded-lg py-2.5 text-sm transition-colors"
            >
              Got it
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6 text-center">
              <h2 className="text-white text-xl font-semibold tracking-tight">Gametrait</h2>
              <p className="text-zinc-400 text-sm mt-1">
                {tab === "login" ? "Welcome back" : "Create your account"}
              </p>
            </div>

            <div className="flex mb-6 bg-zinc-800 rounded-lg p-1">
              {["login", "signup"].map(t => (
                <button key={t} type="button" onClick={() => handleTabSwitch(t)}
                  className={"flex-1 text-sm py-1.5 rounded-md transition-colors " +
                    (tab === t ? "bg-lime-400 text-black font-medium" : "text-zinc-400 hover:text-white")}>
                  {t === "login" ? "Log in" : "Sign up"}
                </button>
              ))}
            </div>

            {error && (
              <div className="mb-4 text-red-400 text-sm bg-red-900/30 border border-red-800 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            {/* ── LOGIN form ──────────────────────────────────────────────── */}
            {tab === "login" && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-zinc-400 text-xs mb-1.5 uppercase tracking-wider">Email</label>
                  <input ref={firstInputRef} type="email" value={email} onChange={e => setEmail(e.target.value)}
                    required autoComplete="email" className={inputCls} placeholder="you@example.com" />
                </div>
                <div>
                  <label className="block text-zinc-400 text-xs mb-1.5 uppercase tracking-wider">Password</label>
                  <div className="relative">
                    <input type={showPass ? "text" : "password"} value={password}
                      onChange={e => setPassword(e.target.value)}
                      required autoComplete="current-password"
                      className={inputCls + " pr-10"} placeholder="••••••••" />
                    <button type="button" onClick={() => setShowPass(v => !v)} className={eyeBtnCls} aria-label="Toggle password visibility">
                      <EyeIcon open={showPass} />
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-lime-400 hover:bg-lime-300 disabled:opacity-50 text-black font-semibold rounded-lg py-2.5 text-sm transition-colors">
                  {loading ? "Logging in…" : "Log in"}
                </button>
                {SignInWithApple && (
                  <>
                    <div className="flex items-center gap-2 my-1">
                      <div className="flex-1 border-t border-zinc-700" />
                      <span className="text-zinc-600 text-xs">or</span>
                      <div className="flex-1 border-t border-zinc-700" />
                    </div>
                    <button type="button" onClick={handleAppleSignIn} disabled={appleLoading}
                      className="w-full flex items-center justify-center gap-2 bg-white hover:bg-zinc-100 text-black font-semibold rounded-lg py-2.5 text-sm transition-colors disabled:opacity-50">
                      <svg width="16" height="16" viewBox="0 0 814 1000" fill="currentColor">
                        <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.6-163.8-123.5C92 375.8 77.3 272.3 77.3 170c0-208.3 135.5-318.6 269-318.6 68.5 0 126.6 42.4 170.2 42.4 43 0 110-44.9 186.7-44.9zm-49.2-137.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
                      </svg>
                      {appleLoading ? "Signing in…" : "Continue with Apple"}
                    </button>
                  </>
                )}

                <p className="text-center text-zinc-500 text-xs">
                  <button onClick={function() { setOpen(false); navigate("/forgot-password"); }}
                    className="hover:text-zinc-300 transition-colors underline underline-offset-2">
                    Forgot password?
                  </button>
                </p>
              </form>
            )}

            {/* ── SIGNUP form ─────────────────────────────────────────────── */}
            {tab === "signup" && (
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="block text-zinc-400 text-xs mb-1.5 uppercase tracking-wider">Alias</label>
                  <input ref={firstInputRef} type="text" value={alias} onChange={e => setAlias(e.target.value)}
                    required autoComplete="username" className={inputCls} placeholder="Your public display name" />
                </div>
                <div>
                  <label className="block text-zinc-400 text-xs mb-1.5 uppercase tracking-wider">Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    required autoComplete="email" className={inputCls} placeholder="you@example.com" />
                </div>
                <div>
                  <label className="block text-zinc-400 text-xs mb-1.5 uppercase tracking-wider">Password</label>
                  <div className="relative">
                    <input type={showPass2 ? "text" : "password"} value={password}
                      onChange={e => setPassword(e.target.value)}
                      required autoComplete="new-password"
                      className={inputCls + " pr-10"} placeholder="At least 8 characters" />
                    <button type="button" onClick={() => setShowPass2(v => !v)} className={eyeBtnCls} aria-label="Toggle password visibility">
                      <EyeIcon open={showPass2} />
                    </button>
                  </div>
                </div>

                {/* ── Date of birth — required, drives minor detection ─── */}
                <div>
                  <label className="block text-zinc-400 text-xs mb-1.5 uppercase tracking-wider">
                    Date of birth <span className="text-zinc-600 normal-case">(required)</span>
                  </label>
                  <input
                    type="date"
                    value={dob}
                    onChange={e => { setDob(e.target.value); setError(""); }}
                    required
                    min={minDateISO()}
                    max={todayISO()}
                    className={inputCls + " [color-scheme:dark]"}
                  />
                  {/* Friendly age label once a valid date is chosen */}
                  {ageOk && (
                    <p className={"text-xs mt-1 " + (isMinor ? "text-amber-400" : "text-zinc-500")}>
                      {isMinor
                        ? ("Under-" + ageThreshold + " accounts require a parent or guardian to approve.")
                        : ""}
                    </p>
                  )}
                </div>

                {/* ── Guardian email — shown for minors or when backend asks ── */}
                {needsParent && (
                  <div>
                    <label className="block text-amber-400 text-xs mb-1.5 uppercase tracking-wider">
                      Parent / guardian email
                    </label>
                    <input
                      type="email"
                      value={parentEmail}
                      onChange={e => setParentEmail(e.target.value)}
                      required
                      autoComplete="off"
                      className={inputCls + " border-amber-700 focus:border-amber-400"}
                      placeholder="parent@example.com"
                    />
                    <p className="text-zinc-600 text-[11px] mt-1 leading-relaxed">
                      We&apos;ll email them for approval before activating the account. No data is shared until they confirm.
                    </p>
                  </div>
                )}

                <button type="submit" disabled={loading}
                  className="w-full bg-lime-400 hover:bg-lime-300 disabled:opacity-50 text-black font-semibold rounded-lg py-2.5 text-sm transition-colors">
                  {loading
                    ? (needsParent ? "Sending request…" : "Creating account…")
                    : (needsParent ? "Send parent request" : "Create account")}
                </button>

                <p className="text-center text-zinc-600 text-[11px] leading-relaxed">
                  By signing up you agree to our{" "}
                  <a href="/terms" className="text-zinc-500 hover:text-zinc-300 underline">Terms</a>{" "}and{" "}
                  <a href="/privacy-policy" className="text-zinc-500 hover:text-zinc-300 underline">Privacy Policy</a>.
                </p>
              </form>
            )}
          </>
        )}

      </div>
    </div>
  );
}